import { FeedbackFormData } from '../../components/feedback-form/feedback-types';
import { maxArrayLength } from '../../utils/validators/maxArrayLength';

export const guidanceFormData: FeedbackFormData = {
  id: 'guidance-data-converter',
  title: $localize`We'd like to hear some more`,
  subtitle: $localize`We promise it will only take a few minutes of your time, and your answers are of high value for us`,
  questions: [
    [
      {
        id: 'statements',
        title: $localize`To what extent do you agree on the following statements`,
        type: 'radio-block',
        options: [
          { id: 'easy', label: $localize`I find this tool easy to use` },
          { id: 'value', label: $localize`A tool like this would add value to my farming operations` },
          { id: 'time', label: $localize`I believe this tool would help me save time` },
          { id: 'errors', label: $localize`I expect this tool to reduce errors` },
          { id: 'independent', label: $localize`I would be less dependent on others thanks to this tool` },
          { id: 'collaboration', label: $localize`A tool like this would ease collaboration with others` },
          { id: 'knowledge', label: $localize`I know which file format works in my terminal` },
        ],
      },
      {
        id: 'would-use',
        title: $localize`Why would or wouldn't you use a tool like this?`,
        subtitle: $localize`Please add a comment below`,
        type: 'textarea',
      },
    ],
    [
      {
        id: 'terminals',
        title: $localize`Which terminal(s) do you use for field operations?`,
        subtitle: $localize`Please select all terminal brands you are using in your daily work`,
        type: 'check-block',
        options: [
          { id: 'agleader', label: $localize`AgLeader` },
          { id: 'case', label: $localize`Case` },
          { id: 'claas', label: $localize`Claas` },
          { id: 'deutz', label: $localize`Deutz` },
          { id: 'fendt', label: $localize`Fendt` },
          { id: 'fj-dynamics', label: $localize`FJ Dynamics` },
          { id: 'john-deere', label: $localize`John Deere` },
          { id: 'massey-ferguson', label: $localize`Massey Ferguson` },
          { id: 'new-holland', label: $localize`New Holland` },
          { id: 'raven', label: $localize`Raven` },
          { id: 'raindancer', label: $localize`Raindancer` },
          { id: 'topcon', label: $localize`TopCon` },
          { id: 'trimble', label: $localize`Trimble` },
          { id: 'valtra', label: $localize`Valtra` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`please specify`,
        },
      },
      {
        id: 'models',
        title: $localize`Please add the modelname of the terminals you're using`,
        subtitle: $localize``,
        type: 'textarea',
      },
    ],
    [
      {
        id: 'conversion-currently',
        title: $localize`How do you currently convert guidance files between different formats?`,
        subtitle: $localize`Select one or more answers`,
        type: 'check-block',
        options: [
          { id: 'not-needed', label: $localize`I don't need to convert guidance files` },
          { id: 'manual', label: $localize`I record the guidance lines in each terminal` },
          {
            id: 'third-party',
            label: $localize`I rely on a third party (e.g., dealer or advisor) to convert the guidance files for me`,
          },
          { id: 'tool', label: $localize`I use a tool to convert my files` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`please specify`,
        },
      },
      {
        id: 'conversion-frequency',
        title: $localize`How often do you need to convert guidance data files?`,
        subtitle: $localize`Select the answer that best applies to your current practice`,
        type: 'radio',
        options: [
          { id: 'daily', label: $localize`Daily` },
          { id: 'weekly', label: $localize`Weekly` },
          { id: 'monthly', label: $localize`Monthly` },
          { id: 'season', label: $localize`A few times per season` },
          { id: 'rarely', label: $localize`Rarely` },
          { id: 'never', label: $localize`Never` },
        ],
      },
    ],
    [
      {
        id: 'features',
        title: $localize`What are the most important features you would expect from a file conversion tool?`,
        subtitle: $localize`Select up to 3 answers`,
        type: 'check-block',
        options: [
          { id: 'formats', label: $localize`Support for many file formats` },
          { id: 'batches', label: $localize`Conversion of multiple files in one workflow` },
          { id: 'help', label: $localize`Help to select the right format for my terminal` },
          { id: 'speed', label: $localize`Fast conversion` },
          { id: 'interface', label: $localize`Easy-to-use interface` },
          { id: 'validation', label: $localize`Error checking/validation` },
          { id: 'connection', label: $localize`Cloud connection (direct file transfer to terminal)` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`please specify`,
        },
        validators: [maxArrayLength(3)],
      },
      {
        id: 'cloud',
        title: $localize`Would you be willing to pay for a cloud connection service that allows automatic file transfer to your terminal?`,
        type: 'radio',
        options: [
          { id: 'yes', label: $localize`Yes` },
          { id: 'maybe', label: $localize`Maybe` },
          { id: 'no', label: $localize`No` },
        ],
      },
    ],
    [
      {
        id: 'scenarios',
        title: $localize`Where do you imagine using a tool like this?`,
        subtitle: $localize`Select one or more answers`,
        type: 'check-block',
        options: [
          { id: 'terminal', label: $localize`In the tractor's terminal` },
          { id: 'web', label: $localize`Online on a webpage` },
          { id: 'integrated', label: $localize`Integrated in my farm management system` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`please specify`,
        },
      },
      {
        id: 'situations',
        title: $localize`In which situations would you need to convert guidance data?`,
        subtitle: $localize`Select one or more answers`,
        type: 'check-block',
        options: [
          { id: 'terminals', label: $localize`When transferring data between different terminals` },
          { id: 'advisor', label: $localize`When sharing data with a contractor or advisor` },
          { id: 'importing', label: $localize`When importing data into a farm management system` },
          { id: 'documentation', label: $localize`When preparing for documentation or reporting` },
          { id: 'external', label: $localize`When using guidance data from external sources` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`please specify`,
        },
      },
    ],
    [
      {
        id: 'relevant-types',
        title: $localize`How relevant do you consider conversion of the data types listed below?`,
        subtitle: $localize`Please rate the relevance of converting each type of data`,
        type: 'radio-block',
        options: [
          { id: 'tasks', label: $localize`Tasks (equipment, material, rates etc.)` },
          { id: 'prescription', label: $localize`Prescription maps` },
          { id: 'coverage', label: $localize`Coverage maps` },
        ],
        values: [
          { id: 'not', label: $localize`Not relevant` },
          { id: 'somewhat', label: $localize`Somewhat relevant` },
          { id: 'highly', label: $localize`Highly relevant` },
        ],
      },
      {
        id: 'additional-types',
        title: $localize`Do you have suggestions for additional file types that might be relevant to convert?`,
        subtitle: $localize``,
        type: 'textarea',
      },
    ],
    [
      {
        id: 'anything-else',
        title: $localize`Anything else you'd like to share`,
        subtitle: $localize`Please add add any other comments, ideas and change requests, you would like to share with us`,
        type: 'textarea',
      },
      {
        id: 'email',
        title: $localize`E-mail contact`,
        subtitle: $localize`In case we need more information, we would like to contact you on the e-mail address you gave us when signing up.`,
        type: 'consent',
        options: [
          { id: 'mail', label: $localize`By ticking this box you allow us to get in contact with you by e-mail.` },
        ],
      },
    ],
  ],
};
